import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies for refresh token
  headers: {
    'Content-Type': 'application/json',
  },
});

// Storage for access token (in-memory, cleared on page refresh)
let accessToken: string | null = null;

// Storage for current organization ID
let currentOrgId: string | null = null;

// Export functions to manage tokens and org
export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const setCurrentOrgId = (orgId: string | null) => {
  currentOrgId = orgId;
};

export const getCurrentOrgId = () => currentOrgId;

// Flag to prevent infinite refresh loops
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor: Add access token and organization ID
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add access token if available
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add organization ID if available
    if (currentOrgId && config.headers) {
      config.headers['X-Organization-Id'] = currentOrgId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401, 403 (2FA required), and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 403 with 2FA_REQUIRED error code - grace period expired
    if (error.response?.status === 403) {
      const data = error.response.data as { detail?: { code?: string } };
      if (data?.detail?.code === '2FA_REQUIRED') {
        // Redirect to 2FA setup screen if not already there
        if (
          window.location.pathname !== '/setup-2fa' &&
          window.location.pathname !== '/login'
        ) {
          window.location.href = '/setup-2fa';
        }
        return Promise.reject(error);
      }
    }

    // Skip token refresh for auth endpoints (login, 2FA verify, etc.)
    const isAuthEndpoint = originalRequest.url?.startsWith('/auth/');

    // If error is 401 and we haven't retried yet (skip for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          undefined,
          {
            withCredentials: true,
          }
        );

        const { access_token } = response.data;
        setAccessToken(access_token);
        isRefreshing = false;

        // Process queued requests
        processQueue(null, access_token);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and reject all queued requests
        processQueue(refreshError as Error, null);
        isRefreshing = false;
        setAccessToken(null);
        setCurrentOrgId(null);

        // Redirect to login only if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
